<template>
  <CheckboxInput
    v-model="scoresStore.onlyKnownUsers"
    class="my-4 font-thin"
    :label="t('pages.wordle.only_known_users_checkbox')"
  ></CheckboxInput>
  <div class="flex items-center justify-between">
    <chevron-left-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="decreaseScoreDate"
    ></chevron-left-icon>
    <h1
      class="text-center text-lg font-medium text-kekkle-dark dark:text-white"
    >
      {{ t("pages.wordle.statistics_off") }} <br />
      <span class="font-bold text-kekkle-dark dark:text-white">
        {{ scoreDate.toLocaleDateString() }}</span
      >
    </h1>
    <chevron-right-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="increaseScoreDate"
    ></chevron-right-icon>
  </div>

  <spinner
    v-if="(fetchingScores || fetchingGameId) && !scores.length"
    class="mx-auto mt-16 w-8 animate-spin text-black-dark dark:text-white"
  ></spinner>

  <div
    v-else
    class="w-full rounded border-2 border-black-dark bg-white p-4 text-kekkle-dark shadow-teal-hard-sm dark:bg-kekkle-dark-light dark:text-white"
  >
    <div>
      <div
        v-for="(score, index) in scores"
        :key="score._id"
        class="relative mb-2 flex flex-nowrap items-center gap-2 p-2"
        :class="{
          'border-b border-black-dark':
            scores[index + 1]?.new_score !== score.new_score,
        }"
      >
        <div class="= flex grow flex-wrap items-center gap-6">
          <p class="text-3xl font-bold">
            <span
              v-if="authStore.authUuid === score.user_id"
              class="pointer text-xl"
              >👉</span
            >
            <span v-if="score.position === 1"> 🥇 </span>
            <span v-else-if="score.position === 2"> 🥈 </span>
            <span v-else-if="score.position === 3"> 🥉 </span>
            <span v-else> {{ score.position }}</span>
          </p>
          <div>
            <p class="">
              {{ t("pages.wordle.name") }}
              <span class="font-bold">{{ score.username }}</span>
            </p>
            <p v-if="groupMembers?.[score.user_id]" class="">
              {{ t("pages.wordle.nick_name") }}
              <span class="font-bold">{{
                groupMembers?.[score.user_id]?.name
              }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.score") }}
              <span class="font-bold">{{ score.new_score }}</span>
            </p>
          </div>
        </div>
        <mini-wordle-board
          :hide-letters="!hasPlayedTodaysGame && lookingAtNewestGameScore"
          :game-state="score.game_state"
        />
      </div>
      <p v-if="!scores.length">{{ t("pages.wordle.no_scores_known") }}</p>
    </div>
  </div>

  <DefaultButton
    v-if="scoresStore.hasMore && scores.length"
    class="mt-4"
    :loading="fetchingScores"
    @click="loadMore"
  >
    {{ t("pages.wordle.load_more") }}
  </DefaultButton>
</template>
<script setup lang="ts">
import MiniWordleBoard from "@/components/wordle/WordleMiniBoard.vue";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useWordle6Store } from "@/stores/modules/wordle6/wordle6";
import { useWordle6ScoresStore } from "@/stores/modules/wordle6/wordle6-scores";
import { useWordle6LeaderboardStore } from "@/stores/modules/wordle6/wordle6-leaderboard";
import { useWordle6MonthlyScoresStore } from "@/stores/modules/wordle6/wordle6-monthly-scores";
import type { WordleGameState } from "@kekkle/shared/types/fields/wordle-game-state/wordle-game-state";
import { computed, watch } from "vue";
import Spinner from "@/components/Spinner.vue";
import { useI18n } from "vue-i18n";
import CheckboxInput from "@/components/Inputs/CheckboxInput.vue";
import { watchDebounced } from "@vueuse/core";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

const { t } = useI18n({ useScope: "global" });

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const wordle6Store = useWordle6Store();
const scoresStore = useWordle6ScoresStore();
const leaderboardStore = useWordle6LeaderboardStore();
const monthlyScoresStore = useWordle6MonthlyScoresStore();

if (wordle6Store.matchDate) {
  scoresStore.setScoreDate(wordle6Store.matchDate);
}
scoresStore.fetchScores(scoresStore.scoreDate);

const changeScoreDate = (days: number): void => {
  const date = new Date(scoresStore.scoreDate);
  date.setDate(date.getDate() + days);
  scoresStore.setScoreDate(date);
};

function increaseScoreDate(): void {
  changeScoreDate(1);
}

function decreaseScoreDate(): void {
  changeScoreDate(-1);
}

watch(
  () => scoresStore.onlyKnownUsers,
  (newValue: boolean) => {
    scoresStore.fetchScores(scoresStore.scoreDate);
    leaderboardStore.setOnlyKnownUsersWordle6Leaderboard(newValue);
    scoresStore.setOnlyKnownUsersWordleScores(newValue);
    monthlyScoresStore.setOnlyKnownUsersWordleMonthlyScores(newValue);
  },
);

watchDebounced(
  () => scoresStore.scoreDate,
  () => {
    scoresStore.fetchScores(scoresStore.scoreDate);
  },
  { debounce: 250 },
);

type ScoredEntry = {
  _id: string;
  user_id: string;
  username: string;
  new_score: number;
  game_state: WordleGameState;
  position: number;
};

const scores = computed<ScoredEntry[]>(() => {
  const raw = scoresStore.currentScores;
  let position = 0;
  let lastScore: number | null = null;

  if (!raw || !raw.length) {
    return [];
  }

  const sortedScores = [...raw].sort(
    (a, b) => (b.new_score ?? 0) - (a.new_score ?? 0),
  );

  return sortedScores.map((score) => {
    const newScore = score.new_score ?? 0;
    if (newScore !== lastScore) {
      position = position + 1;
      lastScore = newScore;
    }

    return {
      ...score,
      new_score: newScore,
      game_state: score.game_state as unknown as WordleGameState,
      position: position,
    };
  });
});

const fetchingScores = computed(() => scoresStore.isFetchingScores);
const fetchingGameId = computed(() => scoresStore.isFetchingGameId);
const groupMembers = computed(() => groupsStore.activeGroupMembers);
const scoreDate = computed(() => scoresStore.scoreDate);

const hasPlayedTodaysGame = computed(() => wordle6Store.hasFinished);
const lookingAtNewestGameScore = computed(() => {
  return (
    wordle6Store.matchId === scoresStore.currentGameId ||
    (wordle6Store.matchDate && wordle6Store.matchDate < scoresStore.scoreDate)
  );
});

function loadMore(): void {
  scoresStore.fetchScores(scoresStore.scoreDate, true);
}
</script>
<style lang="scss">
.pointer {
  font-size: 28px;
}
</style>
